import { Injectable, inject } from "@angular/core";
import {
	HttpErrorResponse,
	HttpEvent,
	HttpHandler,
	HttpInterceptor,
	HttpRequest,
} from "@angular/common/http";
import { Router } from "@angular/router";
import { Observable, catchError, from, switchMap, throwError } from "rxjs";
import { environment } from "../../../environments/environment";
import { isPlanAccessHttpError } from "../../modules/subscription/billing-operations.models";
import { AuthService } from "../services/auth/auth.service";

/** Present on the single retry after a successful refresh; avoids infinite 401→refresh loops. */
const AUTH_RETRY_HEADER = "X-Auth-Retry";

/** After exchanging login JWT → access JWT; retry the request at most once. */
const LOGIN_EXCHANGE_RETRY_HEADER = "X-Login-Exchange-Retry";

/**
 * Prefer `localStorage.token` (set after sign-in / `POST /auth/token`) over `authTokenPair` parsed from
 * `document.cookie`. The cookie is often HttpOnly (unreadable here), and when it is readable it can lag
 * behind or differ from the exchanged access JWT, which yields **200 + empty lists** on tenant-scoped APIs.
 */
function readBearerTokenForApi(): string | null {
	if (typeof window === "undefined") {
		return null;
	}
	const fromLs = localStorage.getItem("token")?.trim();
	if (fromLs) {
		return fromLs;
	}
	return parseAuthTokenPairAccessToken();
}

function parseAuthTokenPairAccessToken(): string | null {
	if (typeof document === "undefined") {
		return null;
	}
	const parts = document.cookie.split("; ");
	for (const part of parts) {
		const eq = part.indexOf("=");
		if (eq < 0) {
			continue;
		}
		const name = part.slice(0, eq).trim();
		if (name !== "authTokenPair") {
			continue;
		}
		let raw = part.slice(eq + 1);
		try {
			raw = decodeURIComponent(raw);
		} catch {
			// use raw
		}
		try {
			const pair = JSON.parse(raw) as {
				accessToken?: string;
				token?: string;
			};
			return (
				pair.accessToken?.trim() ||
				pair.token?.trim() ||
				null
			);
		} catch {
			return null;
		}
	}
	return null;
}

const WORKSPACE_ID_HEADER = "X-Workspace-Id";

function decodeJwtPayloadRecord(token: string): Record<string, unknown> | null {
	try {
		const parts = token.split(".");
		if (parts.length < 2) {
			return null;
		}
		let base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
		const pad = base64.length % 4;
		if (pad) {
			base64 += "=".repeat(4 - pad);
		}
		return JSON.parse(atob(base64)) as Record<string, unknown>;
	} catch {
		return null;
	}
}

/** Workspace id from JWT (Twenty-style `workspaceId`, nested `workspace.id`, etc.). */
function readWorkspaceIdFromJwtPayload(token: string): string | null {
	const payload = decodeJwtPayloadRecord(token);
	if (!payload) {
		return null;
	}
	for (const key of [
		"workspaceId",
		"workspace_id",
		"wid",
		"tenantId",
	] as const) {
		const v = payload[key];
		if (typeof v === "string") {
			const s = v.trim();
			if (s) {
				return s;
			}
		}
	}
	const ws = payload["workspace"];
	if (ws && typeof ws === "object" && !Array.isArray(ws)) {
		const id = (ws as Record<string, unknown>)["id"];
		if (typeof id === "string" && id.trim()) {
			return id.trim();
		}
	}
	return null;
}

function readWorkspaceIdForApi(): string | null {
	if (typeof localStorage === "undefined") {
		return null;
	}
	const fromStorage = localStorage.getItem("wid")?.trim();
	if (fromStorage) {
		return fromStorage;
	}
	const bearer = readBearerTokenForApi();
	if (bearer) {
		const fromJwt = readWorkspaceIdFromJwtPayload(bearer);
		if (fromJwt) {
			return fromJwt;
		}
	}
	const fallback = localStorage.getItem("token")?.trim();
	if (fallback) {
		return readWorkspaceIdFromJwtPayload(fallback);
	}
	return null;
}

@Injectable({ providedIn: "root" })
export class AuthInterceptor implements HttpInterceptor {
	private readonly router = inject(Router);
	private readonly auth = inject(AuthService);

	intercept(
		req: HttpRequest<unknown>,
		next: HttpHandler,
	): Observable<HttpEvent<unknown>> {
		if (this.auth.isGuestAuthRoute() || this.auth.isPreAuthScreen()) {
			return this.handleWithAuth(req, next);
		}
		if (
			this.auth.shouldEnforceMaxSessionAge() &&
			!this.isAuthExempt(req.url) &&
			this.auth.enforceMaxSessionAgeIfExceeded()
		) {
			return throwError(() => new Error("Max session age exceeded"));
		}
		if (this.shouldWaitForAccessToken(req)) {
			return this.auth.waitForAccessTokenRefresh().pipe(
				switchMap(() => this.handleWithAuth(req, next)),
				catchError(() => this.handleWithAuth(req, next)),
			);
		}
		return this.handleWithAuth(req, next);
	}

	private handleWithAuth(
		req: HttpRequest<unknown>,
		next: HttpHandler,
	): Observable<HttpEvent<unknown>> {
		const authorizedRequest = this.appendAuthHeaderIfAvailable(req);
		return next.handle(authorizedRequest).pipe(
			catchError((error: unknown) => {
				if (!(error instanceof HttpErrorResponse)) {
					return throwError(() => error);
				}
				// Expired access JWTs return 403 + "Session Time Out" (not 401). Refresh before logout.
				// After idle, the first request can also fail as 500 (empty body) or a dropped
				// keep-alive connection (status 0); if the local token is actually expired, treat
				// these as recoverable and refresh + retry once instead of failing the request.
				if (
					error.status === 401 ||
					(error.status === 403 && this.isSessionTimeoutMessage(error)) ||
					this.isRecoverableExpiredTokenFailure(error, authorizedRequest)
				) {
					return this.handleUnauthorized(error, authorizedRequest, next);
				}
				if (error.status === 500) {
					const recovered = this.tryRecoverFromLoginJwtThenRetry(
						error,
						authorizedRequest,
						next,
					);
					if (recovered) {
						return recovered;
					}
				}
				return this.propagateAuthError(error, authorizedRequest.url);
			}),
		);
	}

	/** CRM API call with no Bearer yet, but a refresh token exists — wait instead of sending unauthenticated. */
	private shouldWaitForAccessToken(req: HttpRequest<unknown>): boolean {
		if (this.isAuthExempt(req.url) || this.auth.isGuestAuthRoute()) {
			return false;
		}
		if (!this.isCrmApiRequest(req.url)) {
			return false;
		}
		if (req.headers.get(AUTH_RETRY_HEADER) === "1") {
			return false;
		}
		if (readBearerTokenForApi()) {
			return false;
		}
		return this.auth.hasRefreshMaterial();
	}

	private isCrmApiRequest(url: string): boolean {
		const api = environment.apiUrl.replace(/\/$/, "");
		return url.startsWith(api) || url.includes(`${api}/`);
	}

	/**
	 * After the tab is idle, the access JWT expires and the first request(s) can come back
	 * as 401, an empty-body 500, or a dropped keep-alive connection (status 0) — none of
	 * which carry a "Session Time Out" message. When the local token is genuinely expired
	 * and we have refresh material, route these through the refresh + single retry path so
	 * the user doesn't see a failed screen until a later 403 happens to trigger recovery.
	 * Real server errors with a still-valid token are left untouched.
	 */
	private isRecoverableExpiredTokenFailure(
		error: HttpErrorResponse,
		req: HttpRequest<unknown>,
	): boolean {
		const status = error.status;
		const isCandidateStatus =
			status === 0 || status === 401 || status === 403 || status === 500;
		if (!isCandidateStatus) {
			return false;
		}
		if (req.headers.get(AUTH_RETRY_HEADER) === "1") {
			return false;
		}
		if (this.isAuthExempt(req.url) || this.auth.isGuestAuthRoute()) {
			return false;
		}
		if (this.isPermissionError(error, req.url)) {
			return false;
		}
		if (isPlanAccessHttpError(error)) {
			return false;
		}
		if (!this.auth.hasRefreshMaterial() && !readBearerTokenForApi()) {
			return false;
		}
		if (this.isMissingAuthTokenError(error)) {
			return true;
		}
		return this.auth.isAccessTokenExpired();
	}

	private handleUnauthorized(
		error: HttpErrorResponse,
		req: HttpRequest<unknown>,
		next: HttpHandler,
	): Observable<HttpEvent<unknown>> {
		if (this.isAuthExempt(req.url) || this.auth.isGuestAuthRoute()) {
			// Only the 24h clock should end the picker. A leftover "Session Time Out"
			// from the previous session must not log out a fresh sign-in.
			if (
				this.auth.isSelectWorkspaceScreen() &&
				this.auth.isMaxSessionAgeExceeded()
			) {
				this.auth.logout({ showSessionTimeoutToast: true });
			}
			return throwError(() => error);
		}
		/** No session material — do not POST /auth/refresh then logout (invite link / new browser). */
		if (!readBearerTokenForApi() && !this.hasRefreshMaterial()) {
			if (
				this.auth.isOnboardingRoute() &&
				this.isSessionTimeoutMessage(error)
			) {
				this.logoutOnboardingSessionTimeout();
			}
			return throwError(() => error);
		}
		/** Workspace RBAC often returns 401 + JSON message; must not trigger refresh/logout (crm-style). */
		if (this.isPermissionError(error, req.url)) {
			return throwError(() => error);
		}
		if (req.headers.get(AUTH_RETRY_HEADER) === "1") {
			if (
				!this.isPermissionError(error, req.url) &&
				!this.auth.isGuestAuthRoute() &&
				(this.auth.isMaxSessionAgeExceeded() ||
					(this.auth.isOnboardingRoute() &&
						this.isSessionTimeoutMessage(error)))
			) {
				this.logoutOnboardingSessionTimeout();
			}
			return throwError(() => error);
		}
		return this.auth.waitForAccessTokenRefresh().pipe(
			switchMap((token) => {
				const retried = req.clone({
					setHeaders: {
						Authorization: `Bearer ${token}`,
						[AUTH_RETRY_HEADER]: "1",
					},
				});
				return next.handle(retried).pipe(
					catchError((retryErr: unknown) => {
						if (
							retryErr instanceof HttpErrorResponse &&
							!this.isPermissionError(retryErr, retried.url) &&
							!this.auth.isGuestAuthRoute() &&
							(retryErr.status === 401 ||
								(retryErr.status === 403 &&
									this.isSessionTimeoutMessage(retryErr))) &&
							(this.auth.isMaxSessionAgeExceeded() ||
								(this.auth.isOnboardingRoute() &&
									this.isSessionTimeoutMessage(retryErr)))
						) {
							this.logoutOnboardingSessionTimeout();
						}
						return throwError(() => retryErr);
					}),
				);
			}),
			catchError(() => {
				if (
					this.auth.isOnboardingRoute() &&
					this.isSessionTimeoutMessage(error)
				) {
					this.logoutOnboardingSessionTimeout();
				}
				return throwError(() => error);
			}),
		);
	}

	private logoutOnboardingSessionTimeout(): void {
		this.auth.persistLoginReturnUrlForPath(
			this.router.url.split("#")[0],
		);
		this.auth.logout({ showSessionTimeoutToast: true });
	}

	/**
	 * Sign-in sometimes left the **login** JWT in `token`; resource APIs expect **access** JWT.
	 * If the API returns INVALID_JWT_TOKEN_TYPE, exchange via POST /auth/token when we have wid + login material.
	 */
	private tryRecoverFromLoginJwtThenRetry(
		error: HttpErrorResponse,
		req: HttpRequest<unknown>,
		next: HttpHandler,
	): Observable<HttpEvent<unknown>> | null {
		if (req.headers.get(LOGIN_EXCHANGE_RETRY_HEADER) === "1") {
			return null;
		}
		const raw = error.error;
		if (!raw || typeof raw !== "object") {
			return null;
		}
		const body = raw as Record<string, unknown>;
		const errCode = body["error"];
		const msg = String(body["message"] ?? "");
		if (
			errCode !== "INVALID_JWT_TOKEN_TYPE" &&
			!msg.includes("Unsupported token type")
		) {
			return null;
		}
		if (typeof window === "undefined") {
			return null;
		}
		const wid = localStorage.getItem("wid")?.trim();
		if (!wid) {
			return null;
		}
		const loginFromKey = localStorage.getItem("loginToken")?.trim();
		const currentBearer = localStorage.getItem("token")?.trim();
		const loginTokenForExchange = loginFromKey || currentBearer;
		if (!loginTokenForExchange) {
			return null;
		}
		return this.auth.exchangeLoginToken(loginTokenForExchange, wid).pipe(
			switchMap((out) => {
				const access = out.tokens?.accessToken?.token?.trim();
				if (!access) {
					return throwError(() => error);
				}
				this.auth.setLoginToken(access);
				const retried = req.clone({
					setHeaders: {
						Authorization: `Bearer ${access}`,
						[LOGIN_EXCHANGE_RETRY_HEADER]: "1",
					},
				});
				return next.handle(retried);
			}),
			catchError(() => throwError(() => error)),
		);
	}

	private appendAuthHeaderIfAvailable(request: HttpRequest<unknown>): HttpRequest<unknown> {
		if (this.isAuthExempt(request.url) || this.auth.isGuestAuthRoute()) {
			return request;
		}
		const headers: Record<string, string> = {};
		const token = readBearerTokenForApi();
		if (token) {
			headers["Authorization"] = `Bearer ${token}`;
		}
		const workspaceId = readWorkspaceIdForApi();
		if (workspaceId) {
			headers[WORKSPACE_ID_HEADER] = workspaceId;
		}
		if (Object.keys(headers).length === 0) {
			return request;
		}
		return request.clone({ setHeaders: headers });
	}

	/**
	 * Paths that must not send a stale access token and must not trigger refresh
	 * (sign-in, rotation, OTP, OAuth start, login-token exchange).
	 */
	private isAuthExempt(url: string): boolean {
		const paths = [
			"/auth/token",
			"/auth/sign-in",
			"/auth/signup",
			"/auth/refresh",
			"/auth/verify-otp",
			"/auth/resend-otp",
			"/auth/signup-in-workspace",
			/** Public invite landing — must not send stale Bearer/cookies or 401→refresh→logout (Edge). */
			"/auth/workspace",
			"/auth/check-email",
			"/auth/check-invite",
			"/auth/social/",
			"/icp/analyze-from-domain/onboarding",
			"tms-api.katdev.com"
		];
		return paths.some((p) => url.includes(p));
	}

	private propagateAuthError(
		error: HttpErrorResponse,
		url: string,
	): Observable<never> {
		if (this.auth.isGuestAuthRoute()) {
			if (
				this.auth.isSelectWorkspaceScreen() &&
				this.auth.isMaxSessionAgeExceeded()
			) {
				this.auth.logout({ showSessionTimeoutToast: true });
			}
			return throwError(() => error);
		}
		if (this.shouldLogoutOnError(error, url)) {
			const sessionTimedOut = this.isSessionTimeoutMessage(error);
			if (
				sessionTimedOut &&
				this.auth.isOnboardingRoute()
			) {
				this.logoutOnboardingSessionTimeout();
				return throwError(() => error);
			}
			this.auth.logout({
				showSessionTimeoutToast:
					sessionTimedOut &&
					(this.auth.isMaxSessionAgeExceeded() ||
						this.auth.isOnboardingRoute()),
				showInactiveAccountToast: this.isInactiveAccountMessage(error),
				showLicenseRevokedToast: this.isLicenseRevokedMessage(error),
			});
		}
		return throwError(() => error);
	}

	/** Backend often returns HTTP 403 + `message: "Session Time Out"` when the server session expired. */
	private isSessionTimeoutMessage(error: HttpErrorResponse): boolean {
		const body = error.error;
		let message = "";
		if (typeof body === "string" && body.trim()) {
			message = body;
		} else if (body && typeof body === "object" && !Array.isArray(body)) {
			const raw = (body as Record<string, unknown>)["message"];
			message =
				typeof raw === "string"
					? raw
					: Array.isArray(raw)
						? raw.map(String).join(" ")
						: "";
		}
		const n = message.toLowerCase();
		return (
			n.includes("session") &&
			(n.includes("time out") ||
				n.includes("timeout") ||
				n.includes("timed out") ||
				n.includes("expired"))
		);
	}

	private isInactiveAccountMessage(error: HttpErrorResponse): boolean {
		const body = error.error;
		let message = "";
		if (typeof body === "string" && body.trim()) {
			message = body;
		} else if (body && typeof body === "object" && !Array.isArray(body)) {
			const raw = (body as Record<string, unknown>)["message"];
			message =
				typeof raw === "string"
					? raw
					: Array.isArray(raw)
						? raw.map(String).join(" ")
						: "";
		}
		return message.toLowerCase().includes("inactive");
	}

	private isLicenseRevokedMessage(error: HttpErrorResponse): boolean {
		const body = error.error;
		let message = "";
		if (typeof body === "string" && body.trim()) {
			message = body;
		} else if (body && typeof body === "object" && !Array.isArray(body)) {
			const raw = (body as Record<string, unknown>)["message"];
			message =
				typeof raw === "string"
					? raw
					: Array.isArray(raw)
						? raw.map(String).join(" ")
						: "";
		}
		const normalized = message.toLowerCase();
		return (
			normalized.includes("license has been removed") ||
			(normalized.includes("license") && normalized.includes("removed"))
		);
	}

	private shouldLogoutOnError(error: HttpErrorResponse, url: string): boolean {
		if (this.isAuthExempt(url)) {
			return (
				this.auth.isOnboardingRoute() &&
				this.isSessionTimeoutMessage(error)
			);
		}
		if (this.auth.isGuestAuthRoute()) {
			return false;
		}
		if (this.isMissingAuthTokenError(error)) {
			return false;
		}
		if (error.status !== 403) {
			return false;
		}
		if (this.isSessionTimeoutMessage(error) && !this.auth.isMaxSessionAgeExceeded()) {
			return this.auth.isOnboardingRoute();
		}
		if (this.isPermissionError(error, url)) {
			return false;
		}
		if (isPlanAccessHttpError(error)) {
			return false;
		}
		if (this.isAdminContext(url, this.router.url)) {
			return false;
		}
		if (url.includes("/export")) {
			return false;
		}
		return true;
	}

	private isPermissionError(error: HttpErrorResponse, _url: string): boolean {
		if (isPlanAccessHttpError(error)) {
			return true;
		}
		const body = error.error;
		let message: string | undefined;
		if (typeof body === "string" && body.trim()) {
			message = body;
		} else if (body && typeof body === "object" && !Array.isArray(body)) {
			const rec = body as Record<string, unknown>;
			const raw = rec["message"];
			if (typeof raw === "string" && raw.trim()) {
				message = raw;
			} else if (Array.isArray(raw)) {
				message = raw.map(String).join(" ");
			} else {
				const fallback = rec["error"] ?? rec["code"];
				message = typeof fallback === "string" ? fallback : undefined;
			}
		}
		if (!message) {
			return false;
		}
		const normalized = String(message).toLowerCase();
		return (
			normalized.includes("permission") ||
			normalized.includes("not allowed") ||
			normalized.includes("forbidden") ||
			normalized.includes("access denied") ||
			normalized.includes("don't have") ||
			normalized.includes("do not have")
		);
	}

	private isAdminContext(requestUrl: string, currentRouteUrl: string): boolean {
		return (
			requestUrl.includes("/admin/") ||
			requestUrl.includes("/admin/sign-in") ||
			currentRouteUrl.startsWith("/admin")
		);
	}

	/** Backend `JwtAuthGuard` when `Authorization` is absent — not an expired CRM session. */
	private isMissingAuthTokenError(error: HttpErrorResponse): boolean {
		const body = error.error;
		let message = "";
		if (typeof body === "string" && body.trim()) {
			message = body;
		} else if (body && typeof body === "object" && !Array.isArray(body)) {
			const raw = (body as Record<string, unknown>)["message"];
			message =
				typeof raw === "string"
					? raw
					: Array.isArray(raw)
						? raw.map(String).join(" ")
						: "";
		}
		return message.toLowerCase().includes("missing authentication token");
	}

	private hasRefreshMaterial(): boolean {
		if (typeof window === "undefined") {
			return false;
		}
		try {
			if (localStorage.getItem("refresh_token")?.trim()) {
				return true;
			}
		} catch {
			// ignore
		}
		if (typeof document === "undefined") {
			return false;
		}
		return document.cookie.split("; ").some((part) => {
			const eq = part.indexOf("=");
			if (eq < 0) {
				return false;
			}
			const name = part.slice(0, eq).trim();
			return name === "authTokenPair" || name === "refreshToken";
		});
	}
}
